import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface GrowthOptimizer {
  id: string;
  optimizerKey: string;
  name: string;
  optimizerType: string;
  objective: string;
  objectiveJson: string;
  defaultAutomationLevel: string;
  status: string;
  isKillSwitchEnabled: boolean;
}

export interface GrowthOptimizationRun {
  id: string;
  optimizerId: string;
  status: string;
  inputSignalJson: string;
  recommendationJson: string;
  simulateResultJson: string;
  guardrailResultJson: string;
  executionResultJson: string;
  workflowProcessInstanceId?: string | null;
  workflowTaskId?: string | null;
  startedAtUtc: string;
  completedAtUtc?: string | null;
  idempotencyKey: string;
  correlationId?: string | null;
  growthScenarioId?: string | null;
  twinSimulationId?: string | null;
}

export interface GrowthOptimizationGuardrailPolicy {
  id: string;
  policyKey: string;
  appliesToOptimizerTypesJson: string;
  maxBudgetChangePercent?: number | null;
  maxSpendAmount?: number | null;
  maxAudienceBlastRadius?: number | null;
  allowedChannelsJson: string;
  requireSimulation: boolean;
  requireWorkflowApproval: boolean;
  autoInLimitEnabled: boolean;
  isActive: boolean;
}

export interface GrowthOptimizerAutomationSetting {
  id: string;
  optimizerId: string;
  automationLevel: string;
  effectiveFromUtc: string;
  approvedByUserId?: string | null;
  approvalEvidenceJson: string;
  status: string;
}

export interface GrowthOptimizationLiftMeasurement {
  id: string;
  optimizationRunId: string;
  periodStartUtc: string;
  periodEndUtc: string;
  baselineMetricJson: string;
  resultMetricJson: string;
  liftPercent?: number | null;
  attributionRunId?: string | null;
}

@Injectable({ providedIn: 'root' })
export class GrowthOptimizationService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl}/api/v1/growth/optimization`;

  listOptimizers(): Observable<GrowthOptimizer[]> {
    return this.http.get<GrowthOptimizer[]>(`${this.base}/optimizers`);
  }

  getOptimizer(id: string): Observable<GrowthOptimizer> {
    return this.http.get<GrowthOptimizer>(`${this.base}/optimizers/${id}`);
  }

  upsertOptimizer(payload: {
    optimizerKey: string;
    name: string;
    optimizerType: string;
    objective: string;
    objectiveJson: string;
    defaultAutomationLevel: string;
    status: string;
    isKillSwitchEnabled: boolean;
  }): Observable<GrowthOptimizer> {
    return this.http.post<GrowthOptimizer>(`${this.base}/optimizers`, payload);
  }

  updateOptimizer(id: string, payload: {
    optimizerKey: string;
    name: string;
    optimizerType: string;
    objective: string;
    objectiveJson: string;
    defaultAutomationLevel: string;
    status: string;
    isKillSwitchEnabled: boolean;
  }): Observable<GrowthOptimizer> {
    return this.http.put<GrowthOptimizer>(`${this.base}/optimizers/${id}`, payload);
  }

  runOptimizer(id: string, payload: {
    scopeType: string;
    scopeRefId: string;
    periodStartUtc?: string | null;
    periodEndUtc?: string | null;
    objectiveJson: string;
    idempotencyKey: string;
    correlationId?: string | null;
  }): Observable<{ run: GrowthOptimizationRun; isIdempotentReplay: boolean }> {
    return this.http.post<{ run: GrowthOptimizationRun; isIdempotentReplay: boolean }>(`${this.base}/optimizers/${id}/run`, payload);
  }

  listRuns(filter?: { status?: string; optimizerId?: string }): Observable<GrowthOptimizationRun[]> {
    let params = new HttpParams();
    if (filter?.status) params = params.set('status', filter.status);
    if (filter?.optimizerId) params = params.set('optimizerId', filter.optimizerId);
    return this.http.get<GrowthOptimizationRun[]>(`${this.base}/runs`, { params });
  }

  getRun(id: string): Observable<GrowthOptimizationRun> {
    return this.http.get<GrowthOptimizationRun>(`${this.base}/runs/${id}`);
  }

  requestApproval(runId: string): Observable<GrowthOptimizationRun> {
    return this.http.post<GrowthOptimizationRun>(`${this.base}/runs/${runId}/request-approval`, {});
  }

  executeRun(runId: string): Observable<GrowthOptimizationRun> {
    return this.http.post<GrowthOptimizationRun>(`${this.base}/runs/${runId}/execute`, {});
  }

  cancelRun(runId: string): Observable<GrowthOptimizationRun> {
    return this.http.post<GrowthOptimizationRun>(`${this.base}/runs/${runId}/cancel`, {});
  }

  listGuardrails(): Observable<GrowthOptimizationGuardrailPolicy[]> {
    return this.http.get<GrowthOptimizationGuardrailPolicy[]>(`${this.base}/guardrails`);
  }

  upsertGuardrail(payload: {
    policyKey: string;
    appliesToOptimizerTypesJson: string;
    maxBudgetChangePercent?: number | null;
    maxSpendAmount?: number | null;
    maxAudienceBlastRadius?: number | null;
    allowedChannelsJson: string;
    requireSimulation: boolean;
    requireWorkflowApproval: boolean;
    autoInLimitEnabled: boolean;
    isActive: boolean;
  }): Observable<GrowthOptimizationGuardrailPolicy> {
    return this.http.post<GrowthOptimizationGuardrailPolicy>(`${this.base}/guardrails`, payload);
  }

  updateGuardrail(id: string, payload: {
    policyKey: string;
    appliesToOptimizerTypesJson: string;
    maxBudgetChangePercent?: number | null;
    maxSpendAmount?: number | null;
    maxAudienceBlastRadius?: number | null;
    allowedChannelsJson: string;
    requireSimulation: boolean;
    requireWorkflowApproval: boolean;
    autoInLimitEnabled: boolean;
    isActive: boolean;
  }): Observable<GrowthOptimizationGuardrailPolicy> {
    return this.http.put<GrowthOptimizationGuardrailPolicy>(`${this.base}/guardrails/${id}`, payload);
  }

  getAutomationLevel(optimizerId: string): Observable<GrowthOptimizerAutomationSetting> {
    return this.http.get<GrowthOptimizerAutomationSetting>(`${this.base}/optimizers/${optimizerId}/automation-level`);
  }

  updateAutomationLevel(optimizerId: string, payload: {
    requestedLevel: string;
    effectiveFromUtc: string;
    approvalEvidenceJson: string;
  }): Observable<GrowthOptimizerAutomationSetting> {
    return this.http.put<GrowthOptimizerAutomationSetting>(`${this.base}/optimizers/${optimizerId}/automation-level`, payload);
  }

  enableKillSwitch(optimizerId: string, reason: string): Observable<GrowthOptimizer> {
    return this.http.post<GrowthOptimizer>(`${this.base}/optimizers/${optimizerId}/kill-switch`, { reason });
  }

  getLift(runId: string): Observable<GrowthOptimizationLiftMeasurement> {
    return this.http.get<GrowthOptimizationLiftMeasurement>(`${this.base}/runs/${runId}/lift`);
  }

  measureLift(runId: string, payload: {
    periodStartUtc: string;
    periodEndUtc: string;
    baselineMetricJson: string;
    resultMetricJson: string;
    attributionRunId?: string | null;
  }): Observable<GrowthOptimizationLiftMeasurement> {
    return this.http.post<GrowthOptimizationLiftMeasurement>(`${this.base}/runs/${runId}/measure-lift`, payload);
  }
}

export function safeJsonObject(json: string | null | undefined): Record<string, unknown> {
  if (!json?.trim()) return {};
  try {
    const parsed = JSON.parse(json) as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

export function safeJsonArray(json: string | null | undefined): unknown[] {
  if (!json?.trim()) return [];
  try {
    const parsed = JSON.parse(json) as unknown;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
